import { sortBy } from 'lodash'
import { useCallback, useMemo } from 'react'

import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import { useGroup } from '../entities'

const groupUsersFilterQueryParameters = queryParameters().number('page').string('groupuserssearch')

export default function useGroupUsersFilter(
  groupUuid: string,
  { prefix, showMembers, showManagers }: { prefix?: string; showMembers: boolean; showManagers: boolean }
) {
  const queryParameters = useMemo(
    () => (prefix ? groupUsersFilterQueryParameters.prefixed(prefix) : groupUsersFilterQueryParameters),
    [prefix]
  )

  const { page, groupuserssearch, set } = useQueryParameters(queryParameters)
  const setPage = useCallback((page: number) => set({ page }, { preventScrollReset: true }), [set])
  const setSearch = useCallback(
    (search: string) => set({ groupuserssearch: search, page: 0 }, { preventScrollReset: true }),
    [set]
  )

  const { group } = useGroup(groupUuid)

  const allUsers = useMemo(() => {
    const members: {
      [uuid: string]: (typeof group.members)[number] & { isMember?: boolean; isGroupManager?: boolean }
    } = {}

    for (const user of group.members) {
      members[user.uuid] = { ...user, isMember: true }
    }
    for (const manager of group.managers) {
      members[manager.uuid] = members[manager.uuid] || { ...manager }
      members[manager.uuid].isGroupManager = true
    }

    return sortBy(
      Array.from(Object.values(members)).filter(
        ({ isMember, isGroupManager }) => (isMember && showMembers) || (isGroupManager && showManagers)
      ),
      ({ firstname, lastname }) => `${firstname}${lastname}`
    )
  }, [group.members, group.managers, showMembers, showManagers])

  const filteredUsers = useMemo(
    () =>
      groupuserssearch
        ? allUsers.filter(({ firstname, lastname }) =>
            `${firstname} ${lastname}`.toLowerCase().includes(groupuserssearch.toLowerCase())
          )
        : allUsers,
    [allUsers, groupuserssearch]
  )

  const pageCount = Math.ceil(filteredUsers.length / 15)

  const users = useMemo(() => filteredUsers.slice(page * 15, (page + 1) * 15), [filteredUsers, page])

  return {
    page,
    setPage,
    search: groupuserssearch,
    setSearch,
    users,
    pageSize: 15,
    pageCount,
    itemCount: filteredUsers.length,
    result: { isPlaceholderData: group.isDummy },
  }
}
